/**
 * Tap to Pay on iPhone (GH-848): an organization's staff member turns their iPhone into a
 * contactless reader and a donor taps a card, phone or watch.
 *
 * Every route is a **staff** route under `/v1/staff/organizations/:organizationId/terminal/…`,
 * behind `FEATURE_TAP_TO_PAY` and the `donation.take_in_person` permission. The phone never holds
 * a Stripe secret: it holds a short-lived connection token minted here, and a PaymentIntent client
 * secret for exactly one gift. Every amount below is computed by the server; the request carries
 * only the gift the donor chose and whether they cover the processing fee.
 *
 * | Route | What |
 * | --- | --- |
 * | `GET  …/terminal/availability` | Whether this phone may offer Tap to Pay here, and why not |
 * | `GET  …/terminal/preview?amountMinor=` | Both totals for one gift: covered and declined |
 * | `POST …/terminal/connection-token` | The Stripe Terminal connection token and location |
 * | `POST …/terminal/donations` | The gift and its PaymentIntent (idempotent): `card_present` for a tap, `card` for typed entry |
 * | `GET  …/terminal/donations/:donationId` | Where that gift stands (the webhook decides) |
 * | `POST …/terminal/donations/:donationId/cancel` | Withdraw an untapped gift |
 * | `POST …/terminal/donations/:donationId/receipt` | Email the receipt to the donor (optional) |
 *
 * **Typed card entry (GH-949, DEC-057).** The same screen also takes a card typed by hand into
 * Stripe's own PaymentSheet on the phone — never into GiveHere's UI, never through this API. That
 * gift is `entry: "keyed"`: a card-not-present `card` PaymentIntent priced with the online card
 * fee schedule, behind the same gate and permission as a tap. `entry` is optional everywhere and
 * defaults to `"tap"`, so a client that predates it is unaffected.
 */
import { z } from "zod";
import { minorUnitsSchema, moneySchema, stripeModeSchema } from "./v1.js";
import { cardFailureReasonSchema } from "./card-failure.js";
/** Why Tap to Pay is not offered, when it is not. Each has a staff-facing sentence beside it. */
export const TAP_TO_PAY_UNAVAILABLE_REASONS = [
    /** `FEATURE_TAP_TO_PAY` is off on this deployment (production until DEC-047 is decided). */
    "not_enabled",
    /** The caller's role here does not include `donation.take_in_person`. */
    "not_permitted",
    /** The organization cannot accept gifts (unclaimed, suspended, payments off). */
    "organization_not_accepting",
    /** Card payments are off on this deployment (`FEATURE_STRIPE_PAYMENTS`). */
    "card_payments_off",
    /** Stripe needs a street address for the Terminal location, and the organization has none. */
    "address_missing",
];
export const tapToPayUnavailableReasonSchema = z.enum(TAP_TO_PAY_UNAVAILABLE_REASONS);
/**
 * How the card reaches Stripe (GH-949, DEC-057): tapped on the iPhone with Tap to Pay, or typed by
 * the donor or staff member into Stripe's PaymentSheet (card-not-present, not MOTO).
 */
export const TERMINAL_ENTRY_METHODS = ["tap", "keyed"];
export const terminalEntrySchema = z.enum(TERMINAL_ENTRY_METHODS);
/** An event the gift can be for: one the organization's events that is accepting gifts now. */
export const terminalEventOptionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** True for the organization's general fund — "General giving", the default with no event. */
    isGeneralFund: z.boolean(),
});
/** A purpose the gift can be restricted to (DEC-043): the organization's active designations. */
export const terminalDesignationOptionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
export const terminalAvailabilitySchema = z.object({
    available: z.boolean(),
    reason: tapToPayUnavailableReasonSchema.nullable(),
    /** A sentence for the staff member, never the donor. Null when available. */
    message: z.string().nullable(),
    /**
     * The Stripe mode the API runs in (GH-823). `test` lets a DEBUG build use Stripe's simulated
     * Tap to Pay reader; `live` is real cards only. `none` is the synthetic rail, which has no
     * Terminal at all — the app then uses its own simulated reader end to end.
     */
    stripeMode: stripeModeSchema,
    /** The smallest and largest gift the server will price ($1.00 and $100,000 today). */
    minimum: moneySchema,
    maximum: moneySchema,
    /** "2.9% + $0.30" — the same schedule as online giving (DEC-047). */
    feeScheduleLabel: z.string(),
    /** What the Tap to Pay screen shows the donor: the organization's own name. */
    merchantDisplayName: z.string(),
    events: z.array(terminalEventOptionSchema),
    /** The event preselected: the one live event if exactly one is live, else the general fund. */
    defaultEventId: z.string().uuid().nullable(),
    designations: z.array(terminalDesignationOptionSchema),
    /**
     * Whether this staff member may accept Apple's Tap to Pay on iPhone terms for the platform's
     * Stripe account (DEC-047: platform administrators). Decided here, from the staff account
     * itself, because the app's session minted from a donor sign-in carries organization roles
     * only and so never reports platform staff. Accepting the terms is a one-time device step,
     * not cross-tenant reach. Never true for an impersonated session.
     */
    termsAcceptancePermitted: z.boolean(),
    /**
     * GH-949: this server takes a typed card (`entry: "keyed"`) wherever it takes a tap — the same
     * gate, permission and organization checks. Absent from an older server, which the phone reads
     * as "typed entry is not offered" (an older server would ignore `entry` and open a tap intent).
     */
    keyedEntryAvailable: z.boolean().optional(),
});
export const terminalPreviewQuerySchema = z.object({
    amountMinor: minorUnitsSchema,
    /** GH-949: which way the card will be taken; it decides the fee schedule. Default `tap`. */
    entry: terminalEntrySchema.optional(),
});
/**
 * Both of the donor's choices for one gift, priced by the server, shown on the iPhone **before**
 * the tap so the donor decides with the final amount in front of them (Apple's guidance: the
 * total is settled before the Tap to Pay screen opens).
 */
export const terminalPreviewSchema = z.object({
    donation: moneySchema,
    covered: z.object({ costSupport: moneySchema, total: moneySchema }),
    declined: z.object({ fee: moneySchema, total: moneySchema }),
    /** "Cover the $0.61 fee" — the toggle's own label. */
    coverLabel: z.string(),
    /** What declining means: the fee comes out of the gift. */
    declineNote: z.string(),
    /** "Processing fee" — one label everywhere (GH-839). */
    costSupportItemLabel: z.string(),
});
/**
 * The connection token the Stripe Terminal SDK asks for, and the location it is scoped to.
 * Short-lived and single-purpose; never cached by the server and never logged.
 */
export const terminalConnectionTokenSchema = z.object({
    secret: z.string().min(1),
    locationId: z.string().min(1),
});
export const terminalDonationRequestSchema = z.object({
    /** The gift the donor chose, in minor units. Priced by the server; nothing else is trusted. */
    amountMinor: minorUnitsSchema,
    costSupportSelected: z.boolean(),
    designationId: z.string().uuid().nullable().optional(),
    /** Omitted or null: the event preselected by `availability` rules (live event, else general). */
    eventId: z.string().uuid().nullable().optional(),
    /**
     * GH-949: `keyed` opens a card-not-present `card` intent for Stripe's PaymentSheet instead of a
     * `card_present` one for the reader. Omitted is `tap`, exactly as before.
     */
    entry: terminalEntrySchema.optional(),
});
export const terminalDonationResponseSchema = z.object({
    donationId: z.string().uuid(),
    /**
     * A tap: handed to `Terminal.retrievePaymentIntent(clientSecret:)` and nothing else. Typed entry:
     * handed to Stripe's PaymentSheet and nothing else — the card is typed there, never in our UI.
     */
    paymentIntentClientSecret: z.string().min(1),
    /** GH-949: how this gift's card is taken. Absent on a gift made before the field existed. */
    entry: terminalEntrySchema.optional(),
    donation: moneySchema,
    costSupport: moneySchema,
    total: moneySchema,
    eventName: z.string(),
    designationName: z.string().nullable(),
    organizationDisplayName: z.string(),
});
/**
 * Where an in-person gift stands. `completed` only once Stripe's signed webhook has posted it to
 * the ledger — the phone's own "Approved" is the card network's answer, this is the books'.
 */
export const TERMINAL_DONATION_STATES = [
    "awaiting_tap",
    "processing",
    "completed",
    "failed",
    "cancelled",
];
export const terminalDonationStatusSchema = z.object({
    donationId: z.string().uuid(),
    state: z.enum(TERMINAL_DONATION_STATES),
    total: moneySchema,
    /** True once the receipt exists, so the result screen can offer to email it. */
    receiptIssued: z.boolean(),
    /** True once a receipt email has been asked for. */
    receiptEmailRequested: z.boolean(),
    /** Why a `failed` gift failed (GH-945); null otherwise. Words: `CARD_FAILURE_STAFF_COPY`. */
    failureReason: cardFailureReasonSchema.nullable().optional(),
    /**
     * DEC-056: the tap authorized the card and the organization captures at the end of its day.
     * `state` stays `processing`; the result screen may say the charge completes tonight. A receipt
     * email asked for meanwhile is kept and sent when the gift completes. Optional on the wire.
     */
    heldForEndOfDay: z.boolean().optional(),
});
export const terminalCancelResponseSchema = z.object({
    donationId: z.string().uuid(),
    /** `cancelled` when nothing can be charged any more; `processing`/`completed` if it already was. */
    state: z.enum(TERMINAL_DONATION_STATES),
});
export const terminalReceiptRequestSchema = z.object({
    email: z.string().trim().email().max(254),
});
export const terminalReceiptResponseSchema = z.object({
    /** `queued`: the receipt email is on its way. The address is never echoed back. */
    status: z.enum(["queued"]),
});
//# sourceMappingURL=terminal.js.map